/**
 * One-way migration: legacy typed slides → BlockSlide (approximate layout).
 */

import type { Block } from './blocks';
import { createId } from './storage';
import type {
  AskSlide,
  BlockSlide,
  CardsSlide,
  CoverSlide,
  PricingSlide,
  ProblemsSlide,
  QrSlide,
  RightColumnSlide,
  Slide,
  TranscriptSlide,
  TwoColumnSlide,
} from './types';

function tb(
  kind: Extract<Block, { text: string }>['kind'],
  anchor: import('./blocks').Anchor,
  x: number,
  y: number,
  text: string,
  w?: number,
): Block {
  return { id: createId('blk'), kind, anchor, x, y, text, ...(w ? { w } : {}) } as Block;
}

export function convertTypedSlideToBlockSlide(slide: Exclude<Slide, BlockSlide>): BlockSlide {
  const base = { id: slide.id, type: 'blocks' as const, bg: slide.bg, snapEnabled: true };

  switch (slide.type) {
    case 'cover': {
      const s = slide as CoverSlide;
      return {
        ...base,
        preset: 'cover',
        textColor: s.textColor ?? 'light',
        blocks: [
          ...(s.logoSrc
            ? ([
                {
                  id: createId('blk'),
                  kind: 'logo' as const,
                  anchor: 'top-left' as const,
                  x: 10,
                  y: 10,
                  src: s.logoSrc,
                  opacity: 0.9,
                  w: 12,
                },
              ] as Block[])
            : []),
          tb('eyebrow', 'top-left', 10, 14, s.eyebrow),
          tb('headline', 'center-left', 10, 48, s.headline, 52),
          ...(s.subheadline ? [tb('subheadline', 'center-left', 10, 62, s.subheadline, 48)] : []),
          tb('caption', 'bottom-left', 10, 82, s.bylinePrefix ?? ''),
          tb('body', 'bottom-left', 10, 86, s.bylineName ?? ''),
          ...(s.footnote ? [tb('caption', 'bottom-right', 90, 88, s.footnote, 30)] : []),
        ],
      };
    }
    case 'right-column': {
      const s = slide as RightColumnSlide;
      const right = (s.align ?? 'right') === 'right';
      const ax = right ? (88 as const) : (12 as const);
      return {
        ...base,
        preset: 'right-column',
        textColor: s.textColor ?? 'light',
        blocks: [
          tb('eyebrow', right ? 'center-right' : 'center-left', ax, 42, s.eyebrow),
          tb('headline', right ? 'center-right' : 'center-left', ax, 50, s.headline, 38),
          tb('lede', right ? 'center-right' : 'center-left', ax, 62, s.lede, 36),
          ...(s.footnote
            ? [tb('caption', right ? 'center-right' : 'center-left', ax, 82, s.footnote)]
            : []),
        ],
      };
    }
    case 'transcript': {
      const s = slide as TranscriptSlide;
      return {
        ...base,
        preset: 'transcript',
        textColor: s.textColor ?? 'dark',
        blocks: [
          tb('eyebrow', 'center-left', 12, 40, s.eyebrow),
          tb('headline', 'center-left', 12, 48, s.headline, 40),
          ...(s.lede ? [tb('lede', 'center-left', 12, 60, s.lede, 38)] : []),
          {
            id: createId('blk'),
            kind: 'conversation',
            anchor: 'center-right',
            x: 78,
            y: 50,
            w: 32,
            label: '',
            thread: s.messages.map((m) => ({ from: m.sender, text: m.text })),
            footer: s.meta ?? '',
          } as Block,
        ],
      };
    }
    case 'cards': {
      const s = slide as CardsSlide;
      const items = s.cards.map((c) => `${c.title} — ${c.body}`);
      return {
        ...base,
        preset: 'cards',
        textColor: s.textColor ?? 'light',
        blocks: [
          tb('eyebrow', 'center-left', 10, 40, s.eyebrow),
          tb('headline', 'center-left', 10, 48, s.headline, 45),
          ...(s.lede ? [tb('lede', 'center-left', 10, 58, s.lede, 42)] : []),
          {
            id: createId('blk'),
            kind: 'bullets',
            anchor: 'bottom-left',
            x: 10,
            y: 78,
            w: 80,
            items,
          } as Block,
        ],
      };
    }
    case 'qr': {
      const s = slide as QrSlide;
      return {
        ...base,
        preset: 'qr',
        textColor: s.textColor ?? 'light',
        blocks: [
          tb('eyebrow', 'center-left', 12, 44, s.eyebrow),
          tb('headline', 'center-left', 12, 52, s.headline, 40),
          ...(s.lede ? [tb('accent', 'center-left', 12, 62, s.lede)] : []),
          ...(s.body ? [tb('body', 'center-left', 12, 70, s.body, 38)] : []),
          {
            id: createId('blk'),
            kind: 'qr',
            anchor: 'center-right',
            x: 82,
            y: 52,
            value: s.qrValue,
            caption: s.qrCaption,
            w: 22,
          } as Block,
        ],
      };
    }
    case 'problems': {
      const s = slide as ProblemsSlide;
      const items = s.problems.flatMap((p) => [
        `${p.tag}: ${p.title}`,
        ...p.bullets.map((b) => `  → ${b}`),
      ]);
      return {
        ...base,
        preset: 'problems',
        textColor: s.textColor ?? 'light',
        blocks: [
          tb('eyebrow', 'center-right', 88, 36, s.eyebrow),
          tb('headline', 'center-right', 88, 44, s.headline, 40),
          {
            id: createId('blk'),
            kind: 'bullets',
            anchor: 'center-right',
            x: 88,
            y: 58,
            w: 38,
            items,
          } as Block,
        ],
      };
    }
    case 'pricing': {
      const s = slide as PricingSlide;
      const body = s.tiers
        .map(
          (t) =>
            `${t.name} — ${t.price} ${t.priceSuffix ?? ''}\n${t.body}\n${t.features.map((f) => `• ${f}`).join('\n')}`,
        )
        .join('\n\n');
      return {
        ...base,
        preset: 'pricing',
        textColor: s.textColor ?? 'dark',
        blocks: [
          tb('eyebrow', 'top-left', 10, 12, s.eyebrow),
          tb('headline', 'top-left', 10, 18, s.headline, 55),
          ...(s.lede ? [tb('lede', 'top-left', 10, 30, s.lede, 50)] : []),
          tb('body', 'center-left', 10, 48, body, 80),
          ...(s.footer ? [tb('accent', 'bottom-left', 10, 90, s.footer)] : []),
        ],
      };
    }
    case 'two-column': {
      const s = slide as TwoColumnSlide;
      const left = s.columns[0];
      const right = s.columns[1];
      const txt = (c: typeof left) =>
        c ? `${c.title}\n${c.bullets.map((b) => `→ ${b}`).join('\n')}` : '';
      return {
        ...base,
        preset: 'two-column',
        textColor: s.textColor ?? 'dark',
        blocks: [
          tb('eyebrow', 'top-left', 10, 12, s.eyebrow),
          tb('headline', 'top-left', 10, 18, s.headline, 70),
          ...(s.lede ? [tb('lede', 'top-left', 10, 32, s.lede, 55)] : []),
          tb('body', 'center-left', 12, 52, txt(left), 38),
          tb('body', 'center-right', 62, 52, txt(right), 38),
        ],
      };
    }
    case 'ask': {
      const s = slide as AskSlide;
      const right = s.align === 'right';
      const ax = right ? 88 : 12;
      return {
        ...base,
        preset: 'ask',
        textColor: s.textColor ?? 'light',
        blocks: [
          tb('eyebrow', right ? 'center-right' : 'center-left', ax, 44, s.eyebrow),
          tb('headline', right ? 'center-right' : 'center-left', ax, 52, s.headline, 45),
          ...(s.lede
            ? [tb('lede', right ? 'center-right' : 'center-left', ax, 64, s.lede, 42)]
            : []),
          ...(s.contact
            ? [tb('caption', right ? 'center-right' : 'center-left', ax, 80, s.contact)]
            : []),
        ],
      };
    }
    default:
      return {
        ...base,
        preset: 'blank-blocks',
        textColor: 'light',
        blocks: [tb('headline', 'center', 50, 50, 'Converted slide', 50)],
      };
  }
}
